import { MsgType, type NotificationCountType, RoomEvent } from 'matrix-js-sdk';
import type { MatrixEvent } from 'matrix-js-sdk/lib/models/event';
import { backendApi } from '../../utils/backendApi';
import { NotificationReason, RoomNames } from '../../utils/matrixClient';
import { useGetMatrixRoomByName } from './useGetMatrixRoomByName';
import { useMatrixClient } from './useMatrixClient';

export const useActivityProtocol = () => {
  const { client } = useMatrixClient();
  const { getRoom } = useGetMatrixRoomByName();

  const sendMessage = async (
    submissionUuid: string,
    type: NotificationReason,
    message?: string | null,
    clicked = false,
  ) => {
    const room = getRoom(submissionUuid, RoomNames.Submission);
    if (room && client) {
      // Notifications
      void backendApi.notify({ type, submissionUuid });

      // ActivityProtocol
      if (type !== NotificationReason.SubmissionComment && type !== NotificationReason.SubmissionAnswer) {
        return client.sendMessage(room.roomId, {
          body: JSON.stringify({ type, clicked: clicked }),
          formatted_body: message ?? undefined,
          msgtype: MsgType.Text,
        });
      } else if (message) {
        return client.sendMessage(room.roomId, {
          body: message,
          formatted_body: message,
          msgtype: MsgType.Text,
        });
      }
    }
    return null;
  };

  const timelineListener = (submissionUuid: string, callback: (event: MatrixEvent) => void) => {
    const room = getRoom(submissionUuid, RoomNames.Submission);
    if (room && client) {
      room.on(RoomEvent.Timeline, (event) => callback(event));
    }
  };

  const redactionListener = (submissionUuid: string, callback: (event: MatrixEvent) => void) => {
    const room = getRoom(submissionUuid, RoomNames.Submission);
    if (room && client) {
      room.on(RoomEvent.Redaction, (event) => callback(event));
    }
  };

  const unreadNotificationListener = (
    submissionUuid: string,
    callback: (number: Partial<Record<NotificationCountType, number>> | undefined) => void,
  ) => {
    const room = getRoom(submissionUuid, RoomNames.Submission);
    if (room && client) {
      room.on(RoomEvent.UnreadNotifications, (number) => callback(number));
    }
  };

  return {
    sendMessage,
    timelineListener,
    redactionListener,
    unreadNotificationListener,
    redactMessage: backendApi.redactMatrixEvent,
  };
};
